/**
 * Preview warm-up state machine, extracted from PreviewPanel for deterministic
 * unit tests (injected probe/clock/wake). Two phases:
 *  1. PROBE the /preview/<port>/ route until it answers ok — between probes
 *     wait `warmupIntervalMs` OR an external `wake()` (dev-server announce),
 *     whichever first. A probe launched before the preview bridge is wired can
 *     hang in the SW ready-wait up to its cap; waking on the announce re-probes
 *     immediately instead of paying that cap + interval. A deadline with NO ok
 *     probe is `unreachable` — never navigate: the SW's 503 error page commits
 *     like a real document, so the commit phase cannot arbitrate a dead route
 *     (a dead dev server used to show a LIVE preview and measure as a real boot).
 *  2. NAVIGATE the frame after an ok probe, then confirm the document
 *     committed — checked immediately and on every wake/interval tick. A commit
 *     that never happens is `error` — distinct from `unreachable` so the UI
 *     never claims "the route responds" without an observed ok probe.
 */
export interface PreviewWarmupHooks {
  /** One GET probe of the preview route; resolves true on `res.ok`, false on
   * any failure (never throws — the caller's fetch wrapper owns that). */
  readonly probe: (signal: AbortSignal) => Promise<boolean>;
  /** Remount the iframe and start navigation to the preview URL. */
  readonly navigate: () => Promise<void>;
  /** Did the iframe commit a document at the preview URL? */
  readonly committed: () => boolean;
  /** Arm a one-shot external readiness signal; a fresh promise per call. */
  readonly wake: () => Promise<void>;
  readonly sleep: (ms: number) => Promise<void>;
  readonly now: () => number;
}

export interface PreviewWarmupConfig {
  readonly warmupTimeoutMs: number;
  readonly warmupIntervalMs: number;
  readonly probeTimeoutMs: number;
  readonly commitTimeoutMs: number;
  readonly commitIntervalMs: number;
}

export type PreviewWarmupResult = 'live' | 'error' | 'unreachable' | 'cancelled';

export async function runPreviewWarmup(
  hooks: PreviewWarmupHooks,
  cfg: PreviewWarmupConfig,
  isAlive: () => boolean,
): Promise<PreviewWarmupResult> {
  const probeDeadline = hooks.now() + cfg.warmupTimeoutMs;
  const WAKE = Symbol('wake');
  let reachable = false;
  while (isAlive() && hooks.now() < probeDeadline) {
    // ONE wake arm per iteration, shared by the probe race AND the interval
    // sleep below — two arms would leave a gap (probe settled, sleep not yet
    // awaited) where an announce resolves an abandoned promise and is lost.
    const wake = hooks.wake().then(() => WAKE);
    const ac = new AbortController();
    const cap = setTimeout(() => ac.abort(), cfg.probeTimeoutMs);
    let raced: boolean | symbol;
    try {
      // A probe hung in the SW ready-wait must not make the announce wait out
      // probeTimeoutMs — the wake aborts it and re-probes immediately.
      raced = await Promise.race([hooks.probe(ac.signal), wake]);
    } finally {
      clearTimeout(cap);
    }
    if (!isAlive()) return 'cancelled';
    if (raced === true) {
      reachable = true;
      break;
    }
    if (raced === WAKE) {
      ac.abort();
      continue;
    }
    await Promise.race([hooks.sleep(cfg.warmupIntervalMs), wake]);
  }
  if (!isAlive()) return 'cancelled';
  if (!reachable) return 'unreachable';
  await hooks.navigate();
  const commitDeadline = hooks.now() + cfg.commitTimeoutMs;
  while (isAlive()) {
    if (hooks.committed()) return 'live';
    if (hooks.now() >= commitDeadline) return 'error';
    await Promise.race([hooks.sleep(cfg.commitIntervalMs), hooks.wake()]);
  }
  return 'cancelled';
}
